import React, { useState, useEffect, useCallback } from 'react';
import { Save, X, Edit, Trash2, Plus, FileText, Calendar, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import SearchableSelect from '../SearchableSelect';
import MappedTypeSelect from './MappedTypeSelect';
import { formatCurrency as sharedFormatCurrency } from '../../utils/currency';
import {
  getHotelRevenue,
  getHotelRoomStats,
  createHotelRevenue,
  createHotelRoomStats,
  updateHotelRevenue,
  updateHotelRoomStats,
  deleteHotelRevenue,
  deleteHotelRoomStats,
  getRevenueTypes,
  getRoomStatTypes,
  postEtlDlqAction,
} from '../../utils/api';

// Date helpers — handle SQL Server datetime format (2025-08-04 00:00:00.0000000)
const extractDateOnly = (dateString) => {
  if (!dateString) return '';
  if (dateString.includes(' ')) return dateString.split(' ')[0];
  if (dateString.includes('T')) return dateString.split('T')[0];
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const dateOnly = extractDateOnly(dateString);
  const [y, m, d] = dateOnly.split('-').map(Number);
  if (!y || !m || !d) return 'Invalid Date';
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
};

const formatCurrency = (value, currency) => {
  if (value === null || value === undefined) return '';
  return sharedFormatCurrency(value, currency);
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/**
 * Tabbed data editor for revenue and room stats tied to a single import.
 * Props: orgId, locationId, importId, onDataChanged, dropdownZIndex
 */
const ImportDataTabs = ({ stateId, orgId, locationId, importId, onDataChanged, dropdownZIndex = 1000001 }) => {
  const [activeTab, setActiveTab] = useState('revenue');
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState([]);
  const [roomStatsData, setRoomStatsData] = useState([]);
  const [revenueTypes, setRevenueTypes] = useState([]);
  const [roomStatTypes, setRoomStatTypes] = useState([]);

  // Editing state
  const [editingRow, setEditingRow] = useState(null); // { ...rowData }
  const [editingType, setEditingType] = useState(null); // 'revenue' | 'roomStats'
  const [addingRow, setAddingRow] = useState(null);
  const [addingType, setAddingType] = useState(null);

  const fetchData = useCallback(async () => {
    if (!orgId || !locationId || !importId) {
      console.warn('ImportDataTabs: missing required params', { orgId, locationId, importId });
      setLoading(false);
      return;
    }
    setLoading(true);

    // Fetch data and types independently so one failure doesn't kill the rest
    const results = await Promise.allSettled([
      getHotelRevenue({ orgId, locationId, importId }),
      getHotelRoomStats({ orgId, locationId, importId }),
      getRevenueTypes(orgId, locationId),
      // includeMappings — the Maps To column edits roomStatMappedTypes rows.
      getRoomStatTypes(orgId, locationId, false, true),
    ]);

    const [revResult, rsResult, revTypesResult, rsTypesResult] = results;

    if (revResult.status === 'fulfilled') {
      const rev = revResult.value;
      setRevenueData(Array.isArray(rev) ? rev : rev?.data || []);
    } else {
      console.error('ImportDataTabs: failed to load revenue', revResult.reason);
    }

    if (rsResult.status === 'fulfilled') {
      const rs = rsResult.value;
      setRoomStatsData(Array.isArray(rs) ? rs : rs?.data || []);
    } else {
      console.error('ImportDataTabs: failed to load room stats', rsResult.reason);
    }

    if (revTypesResult.status === 'fulfilled') {
      const rt = revTypesResult.value;
      setRevenueTypes(Array.isArray(rt) ? rt : rt?.data || []);
    } else {
      console.error('ImportDataTabs: failed to load revenue types', revTypesResult.reason);
    }

    if (rsTypesResult.status === 'fulfilled') {
      const rst = rsTypesResult.value;
      setRoomStatTypes(Array.isArray(rst) ? rst : rst?.data || []);
    } else {
      console.error('ImportDataTabs: failed to load room stat types', rsTypesResult.reason);
    }

    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      toast.error(`Failed to load ${failures.length} data source(s) — check console`);
    }

    setLoading(false);
  }, [orgId, locationId, importId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Helper: get type name
  const getRevTypeName = (typeId) => {
    const t = revenueTypes.find(rt => rt.id === typeId);
    return t ? (t.name || t.accountName) : typeId || 'N/A';
  };
  const getRevMappedType = (typeId) => {
    const t = revenueTypes.find(rt => rt.id === typeId);
    return t?.mappedType || 'Not Mapped';
  };
  const getRsTypeName = (typeId) => {
    const t = roomStatTypes.find(rt => rt.id === typeId);
    return t ? t.name : typeId || 'N/A';
  };

  // Default date from existing data
  const defaultDate = () => {
    const data = activeTab === 'revenue' ? revenueData : roomStatsData;
    if (data.length > 0) return extractDateOnly(data[0].effectiveDate);
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // ─── CRUD handlers ───

  const handleStartEdit = (row, type) => {
    setEditingRow({ ...row });
    setEditingType(type);
    setAddingRow(null);
    setAddingType(null);
  };

  const handleCancelEdit = () => { setEditingRow(null); setEditingType(null); };

  const refreshDashboardFactDates = useCallback(async (dates, notes) => {
    if (!stateId) return;
    const refreshDates = [...new Set((dates || []).map(extractDateOnly).filter(Boolean))];
    if (refreshDates.length === 0) return;
    try {
      await postEtlDlqAction('refresh_dashboard_fact', {
        stateId,
        refreshDates,
        notes: notes || 'Import review data edit',
      });
    } catch (err) {
      // Do not fail the operator's data edit if the derived dashboard refresh
      // is blocked. The edit succeeded; a scheduled refresh can still repair it.
      console.warn('Dashboard fact refresh after import data edit failed:', err);
    }
  }, [stateId]);

  // A mapped-type assignment changes how this import's dollars roll up, so
  // repair the derived dashboard facts for the dates the retyped rows touch —
  // the same refresh the row edits below trigger.
  const handleRevenueMapped = useCallback((mappedType, typeId) => {
    const dates = revenueData.filter(r => r.revenueTypeId === typeId).map(r => r.effectiveDate);
    refreshDashboardFactDates(dates, 'Import review revenue mapping change');
  }, [revenueData, refreshDashboardFactDates]);

  const handleRoomStatMapped = useCallback((mappedType, typeId) => {
    const dates = roomStatsData.filter(r => r.roomStatTypeId === typeId).map(r => r.effectiveDate);
    refreshDashboardFactDates(dates, 'Import review room stat mapping change');
  }, [roomStatsData, refreshDashboardFactDates]);

  const handleSaveEdit = async () => {
    if (!editingRow) return;
    try {
      const previousRow = editingType === 'revenue'
        ? revenueData.find(r => r.id === editingRow.id)
        : roomStatsData.find(r => r.id === editingRow.id);
      const affectedDates = [previousRow?.effectiveDate, editingRow.effectiveDate];
      if (editingType === 'revenue') {
        const updated = await updateHotelRevenue(editingRow.id, editingRow);
        setRevenueData(prev => prev.map(r => r.id === editingRow.id ? updated : r));
        toast.success('Revenue updated');
      } else {
        const updated = await updateHotelRoomStats(editingRow.id, editingRow);
        setRoomStatsData(prev => prev.map(r => r.id === editingRow.id ? updated : r));
        toast.success('Room stat updated');
      }
      await refreshDashboardFactDates(affectedDates, `Import review ${editingType} edit`);
      setEditingRow(null);
      setEditingType(null);
      onDataChanged?.();
    } catch (err) {
      toast.error('Failed to save: ' + (err.message || 'unknown error'));
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const deletedRow = type === 'revenue'
        ? revenueData.find(r => r.id === id)
        : roomStatsData.find(r => r.id === id);
      if (type === 'revenue') {
        await deleteHotelRevenue(id);
        setRevenueData(prev => prev.filter(r => r.id !== id));
        toast.success('Revenue deleted');
      } else {
        await deleteHotelRoomStats(id);
        setRoomStatsData(prev => prev.filter(r => r.id !== id));
        toast.success('Room stat deleted');
      }
      await refreshDashboardFactDates([deletedRow?.effectiveDate], `Import review ${type} delete`);
      onDataChanged?.();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStartAdd = (type) => {
    setEditingRow(null);
    setEditingType(null);
    if (type === 'revenue') {
      setAddingRow({ effectiveDate: defaultDate(), revenueTypeId: null, currentValue: 0, orgId, locationId, importId });
    } else {
      setAddingRow({ effectiveDate: defaultDate(), roomStatTypeId: null, currentValue: 0, mtd: null, ytd: null, orgId, locationId, importId });
    }
    setAddingType(type);
  };

  const handleCancelAdd = () => { setAddingRow(null); setAddingType(null); };

  const handleSaveAdd = async () => {
    if (!addingRow) return;
    try {
      if (addingType === 'revenue') {
        if (!addingRow.revenueTypeId) { toast.error('Select a revenue type'); return; }
        const created = await createHotelRevenue(addingRow);
        setRevenueData(prev => [created, ...prev]);
        toast.success('Revenue created');
      } else {
        if (!addingRow.roomStatTypeId) { toast.error('Select a room stat type'); return; }
        const created = await createHotelRoomStats(addingRow);
        setRoomStatsData(prev => [created, ...prev]);
        toast.success('Room stat created');
      }
      await refreshDashboardFactDates([addingRow.effectiveDate], `Import review ${addingType} add`);
      setAddingRow(null);
      setAddingType(null);
      onDataChanged?.();
    } catch (err) {
      toast.error('Failed to create: ' + (err.message || 'unknown error'));
    }
  };

  const updateAddField = (field, value) => setAddingRow(prev => ({ ...prev, [field]: value }));
  const updateEditField = (field, value) => setEditingRow(prev => ({ ...prev, [field]: value }));

  // Bulk date change
  const [bulkDate, setBulkDate] = useState('');
  const [bulkDateSaving, setBulkDateSaving] = useState(false);

  const handleBulkDateChange = async () => {
    if (!bulkDate) return;
    const target = activeTab === 'revenue' ? revenueData : roomStatsData;
    if (target.length === 0) { toast.error('No rows to update'); return; }

    setBulkDateSaving(true);
    try {
      const updated = target.map(r => ({ ...r, effectiveDate: bulkDate }));
      const promises = updated.map(r =>
        activeTab === 'revenue' ? updateHotelRevenue(r.id, r) : updateHotelRoomStats(r.id, r)
      );
      await Promise.all(promises);

      if (activeTab === 'revenue') setRevenueData(updated);
      else setRoomStatsData(updated);

      toast.success(`Updated ${updated.length} rows to ${bulkDate}`);
      await refreshDashboardFactDates([...target.map(r => r.effectiveDate), bulkDate], `Import review bulk ${activeTab} date change`);
      setBulkDate('');
      onDataChanged?.();
    } catch (err) {
      toast.error('Failed to update dates');
    } finally {
      setBulkDateSaving(false);
    }
  };

  // ─── Render ───

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-orange-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 py-2 bg-gray-50 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('revenue')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'revenue' ? 'bg-orange-400 text-white' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >Revenue ({revenueData.length})</button>
            <button
              onClick={() => setActiveTab('roomStats')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'roomStats' ? 'bg-orange-400 text-white' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >Room Stats ({roomStatsData.length})</button>
          </div>
          <button
            onClick={() => handleStartAdd(activeTab === 'revenue' ? 'revenue' : 'roomStats')}
            disabled={addingRow !== null || editingRow !== null}
            className="flex items-center gap-1 px-2.5 py-1 bg-orange-400 text-white text-xs font-medium rounded-md hover:bg-orange-500 disabled:opacity-50"
          >
            <Plus size={12} /> Add Row
          </button>
        </div>
        {/* Bulk date change */}
        <div className="flex items-center gap-2">
          <Calendar size={12} className="text-gray-400" />
          <span className="text-[11px] text-gray-500">Set all dates to:</span>
          <input
            type="date"
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
            className="border border-gray-300 rounded px-1.5 py-0.5 text-xs"
          />
          <button
            onClick={handleBulkDateChange}
            disabled={!bulkDate || bulkDateSaving}
            className="flex items-center gap-1 px-2 py-0.5 bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-medium rounded disabled:opacity-50"
          >
            <Check size={11} />
            {bulkDateSaving ? 'Saving...' : 'Apply'}
          </button>
        </div>
      </div>

      {/* Revenue summary cards */}
      {activeTab === 'revenue' && revenueData.length > 0 && (
        <RevenueSummaryCards data={revenueData} getMappedType={getRevMappedType} />
      )}

      {/* Table content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'revenue' ? (
          <RevenueTable
            data={revenueData}
            types={revenueTypes}
            editingRow={editingType === 'revenue' ? editingRow : null}
            addingRow={addingType === 'revenue' ? addingRow : null}
            onStartEdit={(r) => handleStartEdit(r, 'revenue')}
            onCancelEdit={handleCancelEdit}
            onSaveEdit={handleSaveEdit}
            onDelete={(id) => handleDelete(id, 'revenue')}
            onCancelAdd={handleCancelAdd}
            onSaveAdd={handleSaveAdd}
            updateEditField={updateEditField}
            updateAddField={updateAddField}
            getTypeName={getRevTypeName}
            setTypes={setRevenueTypes}
            orgId={orgId}
            onMapped={handleRevenueMapped}
            dropdownZIndex={dropdownZIndex}
          />
        ) : (
          <RoomStatsTable
            data={roomStatsData}
            types={roomStatTypes}
            editingRow={editingType === 'roomStats' ? editingRow : null}
            addingRow={addingType === 'roomStats' ? addingRow : null}
            onStartEdit={(r) => handleStartEdit(r, 'roomStats')}
            onCancelEdit={handleCancelEdit}
            onSaveEdit={handleSaveEdit}
            onDelete={(id) => handleDelete(id, 'roomStats')}
            onCancelAdd={handleCancelAdd}
            onSaveAdd={handleSaveAdd}
            updateEditField={updateEditField}
            updateAddField={updateAddField}
            getTypeName={getRsTypeName}
            setTypes={setRoomStatTypes}
            orgId={orgId}
            locationId={locationId}
            onMapped={handleRoomStatMapped}
            dropdownZIndex={dropdownZIndex}
          />
        )}
      </div>
    </div>
  );
};

// ─── Revenue Summary Cards ───

const fmtDollarCard = (v) =>
  `$${Math.round(v).toLocaleString('en-US')}`;

const RevenueSummaryCards = ({ data, getMappedType }) => {
  const groups = {};
  let grandTotal = 0;

  data.forEach((r) => {
    const mt = getMappedType(r.revenueTypeId);
    if (!mt || mt === 'Not Mapped') return;
    const val = Number(r.currentValue) || 0;
    groups[mt] = (groups[mt] || 0) + val;
    grandTotal += val;
  });

  const entries = Object.entries(groups).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;

  return (
    <div style={{ flexShrink: 0, background: 'var(--lodge-onyx-50)', borderTop: '2px solid var(--lodge-onyx-100)', borderBottom: '1px solid var(--lodge-onyx-100)', padding: '10px 14px 12px' }}>
      {/* Section header — matches "Forecast Total" label style in RevenueForecastModal */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--lodge-onyx-900)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Revenue by Type</span>
        <span style={{ fontSize: 10, color: 'var(--lodge-onyx-400)' }}>mapped only</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
        {entries.map(([mappedType, rev]) => {
          const pct = grandTotal > 0 ? Math.round((rev / grandTotal) * 100) : 0;
          const barColor = pct > 30 ? '#F28C38' : pct > 10 ? '#FDBA74' : '#FDE68A';
          return (
            <div key={mappedType} style={{ background: 'var(--lodge-white)', borderRadius: 7, padding: '8px 10px', border: '1px solid var(--lodge-onyx-100)', minHeight: 72, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--lodge-onyx-600)' }}>{mappedType}</span>
                <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--lodge-onyx-400)', background: 'var(--lodge-onyx-100)', borderRadius: 3, padding: '1px 4px' }}>{pct}%</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--lodge-onyx-900)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{fmtDollarCard(rev)}</div>
              {/* Spacer + track are separate so border-box sizing doesn't collapse the 3px track */}
              <div style={{ marginTop: 'auto', paddingTop: 5 }}>
                <div style={{ height: 3, borderRadius: 2, background: 'var(--lodge-onyx-50)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 2, background: barColor, width: `${Math.max(pct, 2)}%`, transition: 'width 0.3s' }} />
                </div>
              </div>
            </div>
          );
        })}

        {/* Total card — 3px orange top accent + full-width gradient bar */}
        <div style={{ background: 'var(--lodge-white)', borderRadius: 7, padding: '8px 10px', border: '1px solid var(--lodge-onyx-100)', borderTop: '3px solid #F28C38', minHeight: 72, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#F28C38', letterSpacing: '0.04em', marginBottom: 3 }}>TOTAL</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--lodge-onyx-900)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{fmtDollarCard(grandTotal)}</div>
          <div style={{ marginTop: 'auto', paddingTop: 5 }}>
            <div style={{ height: 3, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, #F28C38, #E8722A)', width: '100%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Revenue Table ───

const RevenueTable = ({
  data, types, editingRow, addingRow,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete,
  onCancelAdd, onSaveAdd, updateEditField, updateAddField,
  getTypeName, setTypes, orgId, onMapped, dropdownZIndex,
}) => {
  const [hoverId, setHoverId] = useState(null);

  if (data.length === 0 && !addingRow) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
        <FileText size={32} className="mb-2" />
        <p className="text-sm">No revenue data for this import</p>
      </div>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200 text-xs">
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Date</th>
          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Revenue Type</th>
          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase min-w-[190px]">Maps To</th>
          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase">Amount</th>
          <th className="px-3 py-2 text-center font-medium text-gray-500 uppercase w-20">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {addingRow && (
          <tr className="bg-green-50">
            <td className="px-3 py-2">
              <input type="date" value={addingRow.effectiveDate}
                onChange={(e) => updateAddField('effectiveDate', e.target.value)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full" />
            </td>
            <td className="px-3 py-2">
              <SearchableSelect
                options={types} value={addingRow.revenueTypeId}
                onChange={(v) => updateAddField('revenueTypeId', v)}
                placeholder="Select Type"
                getOptionLabel={(t) => t.name || t.accountName}
                getOptionValue={(t) => t.id}
                emptyMessage="No types"
                dropdownZIndex={dropdownZIndex}
              />
            </td>
            <td className="px-3 py-2">
              <MappedTypeSelect
                typeId={addingRow.revenueTypeId}
                types={types}
                setTypes={setTypes}
                orgId={orgId}
                kind="revenue"
                rowActive
                menuZIndex={dropdownZIndex}
                onAssigned={onMapped}
              />
            </td>
            <td className="px-3 py-2">
              <input type="number" step="0.01" value={addingRow.currentValue || ''}
                onChange={(e) => updateAddField('currentValue', parseFloat(e.target.value) || null)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full text-right" placeholder="0.00" />
            </td>
            <td className="px-3 py-2">
              <div className="flex justify-center gap-1">
                <button onClick={onSaveAdd} className="p-1 text-green-600 hover:text-green-800" title="Save"><Save size={14} /></button>
                <button onClick={onCancelAdd} className="p-1 text-gray-400 hover:text-gray-600" title="Cancel"><X size={14} /></button>
              </div>
            </td>
          </tr>
        )}
        {data.map((r) => {
          const isEditing = editingRow?.id === r.id;
          return (
            <tr
              key={r.id}
              className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}
              onMouseEnter={() => setHoverId(r.id)}
              onMouseLeave={() => setHoverId((prev) => (prev === r.id ? null : prev))}
            >
              <td className="px-3 py-2 whitespace-nowrap">
                {isEditing ? (
                  <input type="date" value={extractDateOnly(editingRow.effectiveDate)}
                    onChange={(e) => updateEditField('effectiveDate', e.target.value)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full" />
                ) : formatDateForDisplay(r.effectiveDate)}
              </td>
              <td className="px-3 py-2">
                {isEditing ? (
                  <SearchableSelect
                    options={types} value={editingRow.revenueTypeId}
                    onChange={(v) => updateEditField('revenueTypeId', v)}
                    placeholder="Select Type"
                    getOptionLabel={(t) => t.name || t.accountName}
                    getOptionValue={(t) => t.id}
                    emptyMessage="No types"
                    dropdownZIndex={dropdownZIndex}
                  />
                ) : getTypeName(r.revenueTypeId)}
              </td>
              <td className="px-3 py-2">
                <MappedTypeSelect
                  typeId={isEditing ? editingRow.revenueTypeId : r.revenueTypeId}
                  types={types}
                  setTypes={setTypes}
                  orgId={orgId}
                  kind="revenue"
                  rowActive={hoverId === r.id}
                  menuZIndex={dropdownZIndex}
                  onAssigned={onMapped}
                />
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {isEditing ? (
                  <input type="number" step="0.01" value={editingRow.currentValue || ''}
                    onChange={(e) => updateEditField('currentValue', parseFloat(e.target.value) || null)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full text-right" />
                ) : formatCurrency(r.currentValue, r.currency)}
              </td>
              <td className="px-3 py-2">
                {isEditing ? (
                  <div className="flex justify-center gap-1">
                    <button onClick={onSaveEdit} className="p-1 text-green-600 hover:text-green-800" title="Save"><Save size={14} /></button>
                    <button onClick={onCancelEdit} className="p-1 text-gray-400 hover:text-gray-600" title="Cancel"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex justify-center gap-1">
                    <button onClick={() => onStartEdit(r)} className="p-1 text-blue-600 hover:text-blue-800" title="Edit"><Edit size={14} /></button>
                    <button onClick={() => onDelete(r.id)} className="p-1 text-red-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ─── Room Stats Table ───

const RoomStatsTable = ({
  data, types, editingRow, addingRow,
  onStartEdit, onCancelEdit, onSaveEdit, onDelete,
  onCancelAdd, onSaveAdd, updateEditField, updateAddField,
  getTypeName, setTypes, orgId, locationId, onMapped, dropdownZIndex,
}) => {
  const [hoverId, setHoverId] = useState(null);

  if (data.length === 0 && !addingRow) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
        <FileText size={32} className="mb-2" />
        <p className="text-sm">No room stats data for this import</p>
      </div>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200 text-xs">
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Date</th>
          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase">Stat Type</th>
          <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase min-w-[190px]">Maps To</th>
          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase">Current</th>
          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase">MTD</th>
          <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase">YTD</th>
          <th className="px-3 py-2 text-center font-medium text-gray-500 uppercase w-20">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {addingRow && (
          <tr className="bg-green-50">
            <td className="px-3 py-2">
              <input type="date" value={addingRow.effectiveDate}
                onChange={(e) => updateAddField('effectiveDate', e.target.value)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full" />
            </td>
            <td className="px-3 py-2">
              <SearchableSelect
                options={types} value={addingRow.roomStatTypeId}
                onChange={(v) => updateAddField('roomStatTypeId', v)}
                placeholder="Select Type"
                getOptionLabel={(t) => t.name}
                getOptionValue={(t) => t.id}
                emptyMessage="No types"
                dropdownZIndex={dropdownZIndex}
              />
            </td>
            <td className="px-3 py-2">
              <MappedTypeSelect
                typeId={addingRow.roomStatTypeId}
                types={types}
                setTypes={setTypes}
                orgId={orgId}
                locationId={locationId}
                kind="roomStats"
                rowActive
                menuZIndex={dropdownZIndex}
                onAssigned={onMapped}
              />
            </td>
            <td className="px-3 py-2">
              <input type="number" step="0.01" value={addingRow.currentValue || ''}
                onChange={(e) => updateAddField('currentValue', parseFloat(e.target.value) || null)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full text-right" placeholder="0.00" />
            </td>
            <td className="px-3 py-2">
              <input type="number" step="0.01" value={addingRow.mtd || ''}
                onChange={(e) => updateAddField('mtd', parseFloat(e.target.value) || null)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-16 text-right" placeholder="0.00" />
            </td>
            <td className="px-3 py-2">
              <input type="number" step="0.01" value={addingRow.ytd || ''}
                onChange={(e) => updateAddField('ytd', parseFloat(e.target.value) || null)}
                className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-16 text-right" placeholder="0.00" />
            </td>
            <td className="px-3 py-2">
              <div className="flex justify-center gap-1">
                <button onClick={onSaveAdd} className="p-1 text-green-600 hover:text-green-800" title="Save"><Save size={14} /></button>
                <button onClick={onCancelAdd} className="p-1 text-gray-400 hover:text-gray-600" title="Cancel"><X size={14} /></button>
              </div>
            </td>
          </tr>
        )}
        {data.map((r) => {
          const isEditing = editingRow?.id === r.id;
          return (
            <tr
              key={r.id}
              className={isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}
              onMouseEnter={() => setHoverId(r.id)}
              onMouseLeave={() => setHoverId((prev) => (prev === r.id ? null : prev))}
            >
              <td className="px-3 py-2 whitespace-nowrap">
                {isEditing ? (
                  <input type="date" value={extractDateOnly(editingRow.effectiveDate)}
                    onChange={(e) => updateEditField('effectiveDate', e.target.value)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full" />
                ) : formatDateForDisplay(r.effectiveDate)}
              </td>
              <td className="px-3 py-2">
                {isEditing ? (
                  <SearchableSelect
                    options={types} value={editingRow.roomStatTypeId}
                    onChange={(v) => updateEditField('roomStatTypeId', v)}
                    placeholder="Select Type"
                    getOptionLabel={(t) => t.name}
                    getOptionValue={(t) => t.id}
                    emptyMessage="No types"
                    dropdownZIndex={dropdownZIndex}
                  />
                ) : getTypeName(r.roomStatTypeId)}
              </td>
              <td className="px-3 py-2">
                <MappedTypeSelect
                  typeId={isEditing ? editingRow.roomStatTypeId : r.roomStatTypeId}
                  types={types}
                  setTypes={setTypes}
                  orgId={orgId}
                  locationId={locationId}
                  kind="roomStats"
                  rowActive={hoverId === r.id}
                  menuZIndex={dropdownZIndex}
                  onAssigned={onMapped}
                />
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {isEditing ? (
                  <input type="number" step="0.01" value={editingRow.currentValue || ''}
                    onChange={(e) => updateEditField('currentValue', parseFloat(e.target.value) || null)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full text-right" />
                ) : formatNumber(r.currentValue)}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {isEditing ? (
                  <input type="number" step="0.01" value={editingRow.mtd || ''}
                    onChange={(e) => updateEditField('mtd', parseFloat(e.target.value) || null)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-16 text-right" />
                ) : formatNumber(r.mtd)}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {isEditing ? (
                  <input type="number" step="0.01" value={editingRow.ytd || ''}
                    onChange={(e) => updateEditField('ytd', parseFloat(e.target.value) || null)}
                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-16 text-right" />
                ) : formatNumber(r.ytd)}
              </td>
              <td className="px-3 py-2">
                {isEditing ? (
                  <div className="flex justify-center gap-1">
                    <button onClick={onSaveEdit} className="p-1 text-green-600 hover:text-green-800" title="Save"><Save size={14} /></button>
                    <button onClick={onCancelEdit} className="p-1 text-gray-400 hover:text-gray-600" title="Cancel"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex justify-center gap-1">
                    <button onClick={() => onStartEdit(r)} className="p-1 text-blue-600 hover:text-blue-800" title="Edit"><Edit size={14} /></button>
                    <button onClick={() => onDelete(r.id)} className="p-1 text-red-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ImportDataTabs;
